import React, { FunctionComponent, ReactNode } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import Card from '@app/components/card/Card';
import { RowSeparator } from '@app/components/Styles';
import Button from '@app/components/Button';
import ChevronDown from '@app/images/chevronDown.svg';
import SVG from '@app/components/SVG';
import useInfraStores from '@app/hooks/useInfraStores';
import OrganizationsSelectionList from '@app/components/sidebar/OrganizationsSelectionList';
import AuthenticatedMode from '@app/login/AuthenticatedMode';
import ButtonThatOpensPopup from '@app/components/popup/ButtonThatOpensPopup';

interface UserInfoProps {
  className?: string;
}

const UserInfo: FunctionComponent<UserInfoProps> = observer((props) => {
  const { authenticationStore, userStore } = useInfraStores<AuthenticatedMode>();

  const renderPopup = (onDone: () => void): ReactNode => {
    const { user, doesUserHaveOrganizations, selectedOrganization } = userStore;

    if (!user) {
      return null;
    }

    const onOtherOrganizationSelected = (organizationId: string): void => {
      userStore.switchOrganizationById(organizationId);
      onDone();
    };

    const onLogoutClick = (): void => {
      authenticationStore.logout();
      onDone();
    };

    const { displayName } = user;

    let otherOrganizations: UserOrganization[] = [];
    let organizationName: undefined | string;

    if (doesUserHaveOrganizations && selectedOrganization) {
      organizationName = selectedOrganization.data.name;
      otherOrganizations = user.organizations.filter((organization) => organization.id !== selectedOrganization.id);
    }

    return (
      <UserPopup shadowType='elevated'>
        <PopupCurrentInfo>
          {organizationName && <PopupOrganizationName>{organizationName}</PopupOrganizationName>}
          <PopupUserName>{displayName}</PopupUserName>
        </PopupCurrentInfo>
        <RowSeparator />
        <StyledOtherOrgsList userOrganizations={otherOrganizations} onOrganizationClicked={onOtherOrganizationSelected} />
        <LogoutButton id='btn-userinfo-logout' appearance='outline' colorScheme='primary' onClick={onLogoutClick}>
          SIGN OUT
        </LogoutButton>
      </UserPopup>
    );
  };

  const { user, doesUserHaveOrganizations, selectedOrganization } = userStore;

  if (!user) {
    return null;
  }

  const { displayName } = user;

  return (
    <UserInfoButton
      dataTestId='btn-user-info-popup-open'
      id='btn-user-info-popup-open'
      popupContent={renderPopup}
      popupPlacement='top-start'
      popupPlacementOffset={{ x: 0, y: 10 }}
      appearance='text'
      colorScheme='white'
      className={props.className}
    >
      <UserSummaryContent>
        <SummaryOrganizationName>{doesUserHaveOrganizations && selectedOrganization?.data.name}</SummaryOrganizationName>
        <SummaryUserName>{displayName}</SummaryUserName>
      </UserSummaryContent>
      <StyledSVG accessibilityLabel='' image={ChevronDown} />
    </UserInfoButton>
  );
});

export default UserInfo;

const UserInfoButton = styled(ButtonThatOpensPopup)`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 150ms;
  cursor: pointer;
  font-family: Inter, system-ui, sans-serif;
  font-weight: normal;
  letter-spacing: normal;
  font-feature-settings: normal;
  font-variant: normal;

  &:hover {
    background-color: #ffffff12;
  }
`;

const UserSummaryContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const UserPopup = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0 0;
  line-height: normal;
  width: 356px;
`;

const SummaryOrganizationName = styled.div`
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #ffffff91;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SummaryUserName = styled.div`
  font-size: 0.75rem;
  line-height: 1rem;
  color: #ffffff52;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PopupCurrentInfo = styled.div`
  text-align: center;
  color: var(--gray-blue-deprecated);
  padding-left: 20px;
  padding-right: 20px;
`;

const PopupOrganizationName = styled.div`
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0.22px;
  margin-bottom: 10px;
`;

const PopupUserName = styled.div`
  font-size: 14px;
  letter-spacing: 0.19px;
  margin-bottom: 16px;
`;

const StyledOtherOrgsList = styled(OrganizationsSelectionList)`
  align-self: stretch;
`;

const LogoutButton = styled(Button)`
  margin: 24px;
`;

const StyledSVG = styled(SVG)`
  min-width: 16px;
  width: 16px;
  height: 16px;
  color: #ffffff52;
  flex-shrink: 0;
`;
